/**
 * Hook for fetching and managing tasks.
 * Supports server-side filtering via GraphQL TaskFilterInput.
 */

import { useQuery, useMutation, gql } from "@apollo/client";
import type { Task, TaskConnection, TaskStatus, TaskCreateInput } from "@/types";
import { CREATE_TASK } from "@/graphql/mutations";

/**
 * Filter parameters that map to the backend GraphQL TaskFilterInput.
 * All fields are optional — only non-null values are sent to the API.
 */
export interface TaskFilterParams {
  search?: string | null;
  projectId?: string | null;
  plotId?: string | null;
  sectionId?: string | null;
  procurementPackageId?: string | null;
  responsibleUserId?: string | null;
  taskType?: string | null;
  dueBefore?: string | null;
  dueAfter?: string | null;
  completedAfter?: string | null;
  parentIdIsNull?: boolean | null;

  // Multi-select filters
  projectIds?: string[];
  plotIds?: string[];
  locationIds?: string[];
  responsibleUserIds?: string[];
  taskTypes?: string[];
  procurementPackageNames?: string[];
  sectionNames?: string[];
  titles?: string[];

  // Deadline-week filter (ISO 8601 week, Helsinki local time)
  deadlineWeekYear?: number | null;
  deadlineWeekNumber?: number | null;
  noDeadline?: boolean | null;

  // Sorting
  sortBy?: string;
  sortDirection?: string;

  // Single-status filter (used by per-column queries)
  status?: string;
  // Multi-status filter — takes precedence over status when set
  statuses?: string[];
}

export const GET_TASKS = gql`
  query GetTasks($limit: Int, $after: String, $filter: TaskFilterInput) {
    tasks(limit: $limit, after: $after, filter: $filter) {
      items {
        id
        title
        description
        status
        taskType
        parentId
        projectId
        sectionId
        locationId
        plotId
        dueDate
        startedAt
        completedAt
        externalOrder
        responsibleUserId
        createdByUserId
        recurrenceSeriesId
        occurrenceDate
        isRecurring
        isOffSchedule
        viewerCanWrite
        externalInspectionPlatformInspectionId
        congridInspectionUrl
        responsibleUser {
          id
          firstName
          lastName
          azureObjectId
          substituteUserId
          substituteFirstName
          substituteLastName
        }
        version
        createdAt
        updatedAt
        procurementPackage {
          code
          name
          plotId
        }
        section {
          id
          name
          code
          procurementPackage {
            code
            name
            plotId
          }
        }
        sitedriveMetadata {
          sitedriveWorkPackage
          sitedriveParentWorkPackage
          sitedriveGrandparentWorkPackage
        }
        location {
          id
          name
          locationType
          hierarchyPath
        }
        project {
          id
          name
        }
        plot {
          id
          name
        }
        subtaskProgress {
          pending
          inProgress
          completed
          problem
          total
        }
      }
      totalCount
      hasNextPage
      endCursor
    }
  }
`;

const GET_SUBTASKS = gql`
  query GetSubtasks($parentId: String!) {
    tasks(filter: { parentId: $parentId }, limit: 100) {
      items {
        id
        title
        description
        status
        taskType
        parentId
        projectId
        sectionId
        locationId
        dueDate
        startedAt
        completedAt
        externalOrder
        externalInspectionPlatformInspectionId
        responsibleUserId
        createdByUserId
        recurrenceSeriesId
        occurrenceDate
        isRecurring
        isOffSchedule
        viewerCanWrite
        responsibleUser {
          id
          firstName
          lastName
          azureObjectId
          substituteUserId
          substituteFirstName
          substituteLastName
        }
        version
        createdAt
        updatedAt
      }
      totalCount
    }
  }
`;

const UPDATE_TASK = gql`
  mutation UpdateTask($input: TaskUpdateInput!) {
    updateTask(input: $input) {
      success
      message
      task {
        id
        title
        description
        status
        taskType
        projectId
        sectionId
        locationId
        dueDate
        startedAt
        completedAt
        externalOrder
        responsibleUserId
        responsibleUser {
          id
          firstName
          lastName
          azureObjectId
          substituteUserId
          substituteFirstName
          substituteLastName
        }
        version
        createdAt
        updatedAt
        procurementPackage {
          code
          name
          plotId
        }
        section {
          id
          name
          procurementPackage {
            code
            name
            plotId
          }
        }
        location {
          id
          name
          locationType
          hierarchyPath
        }
      }
    }
  }
`;


interface TasksQueryResult {
  tasks: TaskConnection;
}

interface TaskUpdateInput {
  id: string;
  version: number;
  title?: string;
  description?: string;
  status?: string;
  priority?: string;
  dueDate?: string;
  scheduledStart?: string;
  scheduledEnd?: string;
}

/**
 * Build a clean GraphQL filter object from TaskFilterParams,
 * omitting null/undefined/empty values.
 */
export function buildGraphQLFilter(params: TaskFilterParams): Record<string, unknown> {
  const filter: Record<string, unknown> = {};

  if (params.parentIdIsNull != null) filter.parentIdIsNull = params.parentIdIsNull;
  if (params.search) filter.search = params.search;
  if (params.projectId) filter.projectId = params.projectId;
  if (params.plotId) filter.plotId = params.plotId;
  if (params.sectionId) filter.sectionId = params.sectionId;
  if (params.procurementPackageId) filter.procurementPackageId = params.procurementPackageId;
  if (params.responsibleUserId) filter.responsibleUserId = params.responsibleUserId;
  if (params.taskType) filter.taskType = params.taskType;
  if (params.dueBefore) filter.dueBefore = params.dueBefore;
  if (params.dueAfter) filter.dueAfter = params.dueAfter;
  if (params.completedAfter) filter.completedAfter = params.completedAfter;

  // Multi-select filters
  if (params.projectIds?.length) filter.projectIds = params.projectIds;
  if (params.plotIds?.length) filter.plotIds = params.plotIds;
  if (params.locationIds?.length) filter.locationIds = params.locationIds;
  if (params.responsibleUserIds?.length) filter.responsibleUserIds = params.responsibleUserIds;
  if (params.taskTypes?.length) filter.taskTypes = params.taskTypes;
  if (params.procurementPackageNames?.length) filter.procurementPackageNames = params.procurementPackageNames;
  if (params.sectionNames?.length) filter.sectionNames = params.sectionNames;
  if (params.titles?.length) filter.titles = params.titles;

  // Deadline-week filter
  if (params.deadlineWeekYear != null) filter.deadlineWeekYear = params.deadlineWeekYear;
  if (params.deadlineWeekNumber != null) filter.deadlineWeekNumber = params.deadlineWeekNumber;
  if (params.noDeadline != null) filter.noDeadline = params.noDeadline;

  // Sorting
  if (params.sortBy) filter.sortBy = params.sortBy;
  if (params.sortDirection) filter.sortDirection = params.sortDirection;

  if (params.statuses?.length) filter.statuses = params.statuses;
  else if (params.status) filter.status = params.status;

  return filter;
}

export function useTasks(filterParams?: TaskFilterParams) {
  const filter = buildGraphQLFilter({
    parentIdIsNull: true,
    ...filterParams,
  });

  const { data, loading, error, refetch } = useQuery<TasksQueryResult>(
    GET_TASKS,
    {
      variables: { limit: 200, filter },
      fetchPolicy: "network-only",
      nextFetchPolicy: "cache-first",
    },
  );

  // Debug logging
  if (data) {
    const parentTasks = data.tasks.items.filter((t: Task) => !t.parentId);
    console.log("Tasks query result:", {
      totalCount: data.tasks.totalCount,
      hasNextPage: data.tasks.hasNextPage,
      itemCount: data.tasks.items.length,
      parentTasks: parentTasks.length,
      parentTaskStatuses: parentTasks.map((t: Task) => ({ id: t.id, status: t.status })),
    });
  }

  const [updateTaskMutation] = useMutation(UPDATE_TASK, {
    refetchQueries: [{ query: GET_TASKS, variables: { limit: 200, filter } }],
    awaitRefetchQueries: true,
  });

  const [createTaskMutation] = useMutation(CREATE_TASK, {
    refetchQueries: [{ query: GET_TASKS, variables: { limit: 200, filter } }],
    awaitRefetchQueries: true,
  });

  const updateTask = async (input: TaskUpdateInput) => {
    console.log("[useTasks] Updating task:", input);
    const result = await updateTaskMutation({ variables: { input } });
    console.log("[useTasks] Update result:", result.data?.updateTask);
    return result.data?.updateTask;
  };

  const updateTaskStatus = async (
    taskId: string,
    status: TaskStatus,
    version: number,
  ) => {
    return updateTask({
      id: taskId,
      version,
      status: status,
    });
  };

  const createTask = async (input: TaskCreateInput) => {
    console.log("[useTasks] Creating task:", input);
    const result = await createTaskMutation({ variables: { input } });
    console.log("[useTasks] Create result:", result.data?.createTask);
    return result.data?.createTask;
  };

  return {
    tasks: (data?.tasks.items.filter((task: Task) => !task.parentId) || []) as Task[],
    totalCount: data?.tasks.totalCount || 0,
    hasNextPage: data?.tasks.hasNextPage || false,
    loading,
    error,
    refetch,
    updateTask,
    updateTaskStatus,
    createTask,
    fetchTasksQuery: GET_TASKS,
  };
}

/**
 * Standalone mutations hook — no refetchQueries.
 * Callers are responsible for triggering refetch after mutations succeed.
 */
export function useTaskMutations() {
  const [updateTaskMutation] = useMutation(UPDATE_TASK);
  const [createTaskMutation] = useMutation(CREATE_TASK);

  const updateTask = async (input: TaskUpdateInput) => {
    const result = await updateTaskMutation({ variables: { input } });
    return result.data?.updateTask;
  };

  const updateTaskStatus = async (taskId: string, status: TaskStatus, version: number) => {
    return updateTask({ id: taskId, version, status });
  };

  const createTask = async (input: TaskCreateInput) => {
    const result = await createTaskMutation({ variables: { input } });
    return result.data?.createTask;
  };

  return { updateTask, updateTaskStatus, createTask };
}

export function useSubtasks(parentId: string | null) {
  const { data, loading, error, refetch } = useQuery<TasksQueryResult>(
    GET_SUBTASKS,
    {
      variables: { parentId },
      skip: !parentId,
      fetchPolicy: "cache-and-network",
    },
  );

  return {
    subtasks: (data?.tasks.items || []) as Task[],
    loading,
    error,
    refetch,
  };
}
